/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

(function () {
  /** Disabled feature - load product sibling using ajax
  $('#product-form-container').on('click','.sibling-product-url',function(e) {
    const $productFormContainer = $(this).closest('#product-form-container');
    
    if($(this).data('product-handle')) {
      e.preventDefault();
      var href = $(this).attr('href');
      var product_handle = $(this).data('product-handle');
      $smile_clone = $productFormContainer.find('[id*="smile_io_smile_points_on_product_page"]').clone();
      
      $.ajax({
       url: href,
       dataType: "html",
       type:'GET',
       success: function(data){
         var productFormHTML = $(data).find('#product-form-container');
         $smile_orig = productFormHTML.find('[id*="smile_io_smile_points_on_product_page"]');
         $smile_orig_parent = $smile_orig.parent();
         $smile_orig.remove();
         $smile_orig_parent.append($smile_clone);
         $('#product-form-container').html(productFormHTML.html());
         window.history.replaceState({data:'test'}, "", href);
          ks.loadSizing({
            productData: {
              productID: $(productFormHTML).attr('data-product-id'),
              title: $(productFormHTML).attr('data-title'),
              images: $(productFormHTML).attr('data-images').split(',')
            }
          });

          const addToCartBtnList = document.querySelectorAll('.add-to-cart');
          Array.from(addToCartBtnList).forEach((addToCartBtn) => {
            addToCartBtn.addEventListener('click', (event) => {
              const form = document.querySelector('.js-product-form');
              const sizeField = document.querySelector('[data-option="Size"]');
              const isSizeSelected = sizeField.querySelector('input[type="radio"]:checked');
              const isValidForm = form.checkValidity();
              if (isValidForm === false || !!isSizeSelected === false) {
                event.preventDefault();
                const errorNotif = document.querySelector('#no-size-selected');
                errorNotif.classList.add('show')
                const variantPicker = document.querySelector('variant-picker');
                event.preventDefault();
                if(window.screen.width < 768) {
                   errorNotif.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                  });
                } else {
                    if(addToCartBtn.classList.contains('sticky-btn')) {
                      errorNotif.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                      });
                    } 
                }
              }
            });
          });

          document.dispatchEvent(new CustomEvent("swym:collections-loaded"));
         //  SmileUI.init({
         //    channel_key: "channel_6o76t1OCx8RnRiGs3I9KCqk9"
         //  })
         // SmileUI.ready().then(smileUiInstance => {
         //   console.log('asdasdasd')
         //   loadSmileScript();
         // });
          
        function updateFitSizeDefault() {
          const stickyAddToCartBtn = document.querySelector('.sticky-atc__buttons .add-to-cart');
          stickyAddToCartBtn.dataset.addToCartText = 'Select a size';
          stickyAddToCartBtn.textContent = 'Select a size';
          var fit_wrap = document.querySelector('[data-option="Select Fit"]');
          var fit_input = fit_wrap.querySelectorAll('input');
          fit_input.forEach((fit) => {
            fit.addEventListener('click', () => {
              setTimeout(() => {
                var wrap = document.querySelector('[data-option="Size"]');
                var inputElements = wrap.querySelectorAll('input');
                var is_fitChecked = wrap.querySelector('input[type="radio"]:checked')
                console.log('fit checked', is_fitChecked)
                if (is_fitChecked) {
                  is_fitChecked.checked = false;
        
                  const addToCartBtn = document.querySelector('.buy-buttons-row .add-to-cart');
                  addToCartBtn.dataset.addToCartText = 'Select a size';
                  addToCartBtn.textContent = 'Select a size';
                  addToCartBtn.removeAttribute('disabled');
        
                  const stickyAddToCartBtn = document.querySelector('.sticky-atc__buttons .add-to-cart');
                  stickyAddToCartBtn.dataset.addToCartText = 'Select a size';
                  stickyAddToCartBtn.textContent = 'Select a size';
                }
                if(is_fitChecked && is_fitChecked.classList.contains('is-unavailable')) {
                  for (const input of inputElements) {
                    if (!input.classList.contains('is-unavailable')) {
                      var input_id = input.getAttribute('id')
                      var label = wrap.querySelector(`label[for=${input_id}]`)
                      // label.click();
                      break;
                    }
                  }
                }
                const errorNotif = document.querySelector('#no-size-selected');
                errorNotif.classList.remove('show')
              }, 300)
        
            })
          })
        }
        
        function validateSizeSelect() {
          const sizeSelectors = document.querySelectorAll('[data-option="Size"] .opt-label');
          if (sizeSelectors.length !== 0) {
            sizeSelectors.forEach((option) => {
              option.addEventListener('click', (event) => {
                event.stopPropagation();
                const errorNotif = document.querySelector('#no-size-selected');
                errorNotif.classList.remove('show')
                const addToCartBtn = document.querySelector('.buy-buttons-row .add-to-cart');
                addToCartBtn.dataset.addToCartText = 'Add to cart';
                const stickyAddToCartBtn = document.querySelector('.sticky-atc__buttons .add-to-cart');
                stickyAddToCartBtn.dataset.addToCartText = 'Add to cart';
        
              })
            })

            const stickyText = document.querySelector('.sticky-atc__details__variant');
            let stickyAddToCartBtn = document.querySelector('.sticky-atc__buttons .add-to-cart');
            stickyAddToCartBtn.textContent = 'Select a size';
            if (stickyText) {
              stickyText.setAttribute("hidden", "");
            }
          }
        }
        
        function disableDefaultValidation() {
          document.addEventListener('invalid', (function () {
            return function (e) {
              e.preventDefault();
            };
          })(), true);
        }
        
        const observer = new MutationObserver(() => {
          console.log("Mutation observer fired.");
        });
        
        const targetElement = document.getElementById('product-form-container');
        
        if (targetElement) {
          observer.observe(targetElement, {
            attributes: true,
            childList: true,
            subtree: true,
            characterData: true
          });
          if(document.getElementById('hide-fit__sizes')) {
            updateFitSizeDefault();
          } else if(document.querySelector('.hide-fit__sizes')) {
            updateFitSizeDefault();
          }
          validateSizeSelect();
          disableDefaultValidation();
          observer.disconnect();
          console.log("Mutation observer disconnected.");
          
        } else {
          console.log("#product-form-container not found. Observer not attached.");
          updateFitSizeDefault();
          validateSizeSelect();
          disableDefaultValidation();
        }
    }
  });
    }
  })
**/

  $('#product-form-container').on('on:variant:change','variant-picker',function(e) {
    
  });

  document.addEventListener('on:quickbuy:after-open', (event) => {
     const addToCartBtnList = document.querySelectorAll('.add-to-cart');
      Array.from(addToCartBtnList).forEach((addToCartBtn) => {
        addToCartBtn.addEventListener('click', (event) => {
          const form = document.querySelector('.js-product-form');
          const sizeField = document.querySelector('[data-option="Size"]');
          const isSizeSelected = sizeField.querySelector('input[type="radio"]:checked');
          const isValidForm = form.checkValidity();
          if (isValidForm === false || !!isSizeSelected === false) {
            event.preventDefault();
            const errorNotif = document.querySelector('#no-size-selected');
            errorNotif.classList.add('show')
            const variantPicker = document.querySelector('variant-picker');
            event.preventDefault();
            if(window.screen.width < 768) {
                errorNotif.scrollIntoView({
                  behavior: 'smooth',
                  block: 'center'
                });
            }        
          }
        });
      });
   });
})();
